import { Component, Vue } from 'vue-property-decorator';
import { UsersHeader, UsersFilter, UsersList } from '@/components/users';
import UsersStore from '@/store/modules/UsersStoreModule';
import UserDetailsStore from '@/store/modules/UserDetailsStoreModule';
import TenantDetailsStore from '@/store/modules/TenantDetailsStoreModule';
import { UsersStoreModule, TenantDetailsStoreModule, UserDetailsStoreModule, AppStoreModule } from '@/store';
import IAdminService from '@/services/http/interfaces/IAdminService';
import { TenantDetailsApi } from '@/common/models';
import { UsersForm, UserDetailsForm } from '@/common/viewModels';
import { ToastHelper, ErrorHelper } from '@/common/helpers';
import { TenantUser } from '@/common/models';
import AppStore from '@/store/modules/AppStoreModule';

@Component({
  components: {
    UsersHeader,
    UsersFilter,
    UsersList,
  },
})

export default class Users extends Vue {
  app$: AppStore = AppStoreModule;
  users$: UsersStore = UsersStoreModule;
  userDetails$: UserDetailsStore = UserDetailsStoreModule;
  tenantDetails$: TenantDetailsStore = TenantDetailsStoreModule;
  isSelfInviteEnabled: boolean = false;

  private adminService?: IAdminService;

  get tenantId(): string {
    return this.$route.params.tenantId;
  }

  get tenantDetails(): TenantDetailsApi | null {
    return this.tenantDetails$.tenantDetails;
  }

  get form(): UsersForm {
    return this.users$.form;
  }

  get editForm(): UserDetailsForm {
    return this.userDetails$.editForm;
  }

  get tenantUserInviteError(): string {
    return this.users$.tenantUserInviteError;
  }

  get tenantUserEditError(): string {
    return this.userDetails$.tenantUserEditError;
  }

  get tenantUserDeleteError(): string {
    return this.userDetails$.tenantUserDeleteError;
  }

  get isLoading(): boolean {
    return this.app$.isLoading;
  }

  get users(): TenantUser[] {
    return this.users$.users ? this.users$.users : [];
  }

  get filteredUsers(): TenantUser[] {
    return this.users$.filteredUsers ? this.users$.filteredUsers : [];
  }

  get disableActionBtn(): boolean {
    return !this.users$.isInviteUserFormValid;
  }

  get isEditUserFormValid(): boolean {
    return this.userDetails$.isEditTenantFormValid;
  }

  mounted() {
    this.initServices();

    Promise.all([
      this.getUsers(),
      this.tenantDetails$.getTenantDetails({ adminService: this.adminService!, tenantId: this.tenantId }),
    ]);
  }

  initServices(): void {
    this.adminService = this.$factory.createAdminService();
    this.getConfiguration();
  }

  filterChange(filter: string): void {
    this.users$.setFilter(filter);
  }

  pageChange(page: number): void {
    this.users$.setCurrentPage(page);
  }

  goBack(): void {
    this.$router.back();
  }

  hideSelfInviteConfirmModal(): void {
    (this.$refs.usersHeader as UsersHeader).hideSelfInviteConfirmModal();
  }

  hideInviteUserModal(): void {
    (this.$refs.usersHeader as UsersHeader).hideInviteUserModal();
  }

  hideEditUserModal(): void {
    (this.$refs.usersList as UsersList).hideEditUserModal();
  }

  hideDeleteUserModal(): void {
    (this.$refs.usersList as UsersList).hideDeleteUserModal();
  }

  onResetInviteUserForm(): void {
    this.users$.resetInviteUserModalForm();
  }

  async onInviteUserFormInputChange(fieldName: string, value: any): Promise<void> {
    this.users$.inviteUserModalFormInputChange({ fieldName, value });
    // this will change the state of the form and can be used to enable the action button
    this.users$.SET_INVITE_USER_FORM_DO_VALIDATION(true);
    await Promise.all([
      this.users$.UPDATE_INVITE_USER_FORM_STATE(),
    ]);
  }

  onEditUserFormInputChange(fieldName: string, value: any): void {
    this.userDetails$.editUserModalFormInputChange({ fieldName, value });
  }

  async getUsers(): Promise<void> {
    await this.users$.getUsers({ adminService: this.adminService!, tenantId: this.tenantId });
  }

  async getConfiguration(): Promise<void> {
    const config = await this.adminService?.getConfigDetails();
    this.isSelfInviteEnabled = config?.isSelfInviteEnabled ?? false;
  }

  async onSelectedUser(userId: string): Promise<void> {
    await this.userDetails$.getUser({ adminService: this.adminService!, tenantId: this.tenantId, userId });
    await this.userDetails$.UPDATE_EDIT_TENANT_USER_FORM_STATE();
    (this.$refs.usersList as UsersList).showEditUserModal();
  }

  userClicked(tenantId: string, userId: string): void {
    this.$router.push({
      name: 'UserDetails',
      params: {
        tenantId,
        userId,
      },
    });
  }

  async onSelectDeleteUser(user: TenantUser): Promise<void> {
    await this.userDetails$.setDeleteUser(user);
    (this.$refs.usersList as UsersList).showDeleteUserModal();
  }

  async onInviteUserToTenancy(): Promise<void> {
    try {
      const isSuccess = await this.users$.inviteUserToTenancy({ adminService: this.adminService!, tenantId: this.tenantId });
      if (isSuccess) {
        this.hideInviteUserModal();
        ToastHelper.Notify('User invited', `The user ${this.users$.inviteUserEmail} has been invited successfully.`);
        await this.getUsers();
      }
    } catch (error) {
      this.users$.updateTenantUserInviteError(ErrorHelper.getMessageForError(error));
    }
  }

  async onEditTenantUser(): Promise<void> {
    try {
      const isSuccess: boolean = await this.userDetails$.requestTenantUserEdit({ adminService: this.adminService!, tenantId: this.tenantId });
      if (isSuccess) {
        this.hideEditUserModal();
        ToastHelper.Notify('User updated', `The user ${this.userDetails$.user!.email} has been updated successfully.`);
        await this.getUsers();
      }
    } catch (error) {
      this.userDetails$.updateEditTenantUserError(ErrorHelper.getMessageForError(error));
    }
  }

  async onDeleteTenantUser(): Promise<void> {
    try {
      const isSuccess = await this.userDetails$.requestTenantUserDelete({ adminService: this.adminService!, tenantId: this.tenantId });
      if (isSuccess) {
        this.hideDeleteUserModal();
        ToastHelper.Notify('User deleted', `The user ${this.userDetails$.user?.email} has been deleted successfully.`);
        await this.getUsers();
      }
    } catch (error) {
      this.userDetails$.updateDeleteTenantUserError(ErrorHelper.getMessageForError(error));
    }
  }

  async selfInvite(): Promise<void> {
    try {
      const isSuccess = await this.users$.selfInvite({ adminService: this.adminService!, tenantId: this.tenantId });
      if (isSuccess) {
        this.hideSelfInviteConfirmModal();
        ToastHelper.Notify('User invited', `The user ${this.app$.user.email?.toLowerCase()} has been invited successfully.`);
        await this.getUsers();
      }
    } catch (error) {
      this.users$.updateTenantUserInviteError(ErrorHelper.getMessageForError(error));
    }
  }
}
